import { useState, useCallback } from "react";
import { ospThemes, Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import log from "logbeacon";
import { Input } from "../Watchlist/Input";
import { QuickStartSearchItem } from "./type";

// 引入拆分后的组件和 Hook
import { SearchOverview } from "./SearchOverview";
import { CategoryDetail } from "./CategoryDetail";
import { RecommendedArea } from "./RecommendedArea";
import { ViewState, CategoryType } from "./constants";
import { useSearchLogic } from "./useSearchLogic";
import { useRecommendedData } from "./useRecommendedData";
import { DashboardSortByTimeType } from "@/src/hooks";
import type { ThemeMarketType } from "@/src/const";

import Translations from "@translate/components/QuickInsights/QuickSearchNormalComponent";
import { I18nUtil } from "@utils/i18n";
import { StockType } from "@/src/utils/stockType";
const I18n = I18nUtil.createScoped(Translations);

export interface PricePredictionProps {
  onStartSearch?: (item: QuickStartSearchItem) => void;
  type?: ThemeMarketType;
  market?: StockType;
  sort?: DashboardSortByTimeType;
  showCancel?: boolean;
  onClose?: () => void;
  title?: string;
}

const QuickSearchNormalComponent = ({
  onStartSearch,
  type = "crypto",
  sort,
  showCancel,
  onClose,
  title,
}: PricePredictionProps) => {
  const [onFocus, setOnFocus] = useState(false);
  const [recommendedTab, setRecommendedTab] = useState<
    "crypto" | "hk-stocks" | "us-stocks"
  >(type);

  // 使用搜索逻辑 Hook
  const {
    keywords,
    viewState,
    selectedCategory,
    contentScrollRef,
    cryptoSearchData,
    stockSearchData,
    searchLoading,
    handleSearchInput,
    handleClearSearch,
    handleViewAll,
    handleBack,
  } = useSearchLogic();

  // 使用推荐数据 Hook
  const {
    hotPageData,
    hotPageDataLoading,
    trendingProjectsDataSet,
    hotXRequestAgentLoading,
    stockTrending,
    stockTrendingRequestAgentLoading,
  } = useRecommendedData(recommendedTab, sort);

  // 处理搜索结果项点击
  const handleItemClick = useCallback(
    (item: any, category?: CategoryType) => {
      const actualCategory = category || selectedCategory;
      log.debug("[文案]handleItemClick item:", item, actualCategory);
      onStartSearch?.({
        name: item?.symbol || item?.project_name || "",
        address:
          actualCategory === CategoryType.CRYPTO ? item?.address || "" : "",
        symbol: item?.symbol || item?.token_symbol || "",
        id: item?.id || item?.contract_address?.[0]?.id || "",
        category: actualCategory || undefined,
      });
    },
    [onStartSearch, selectedCategory],
  );

  return (
    <div className="flex flex-col h-full relative w-full">
      {/* 搜索输入区域 - 吸顶 */}
      <div
        className={cn(
          "sticky top-0 z-11 flex flex-row items-center gap-3 bg-background_solid_secondary",
          {
            "md:py-6 pt-4 md:pt-0 pb-4": !showCancel,
            "md:py-6 pt-4 md:pt-0 py-4": showCancel,
          },
        )}
      >
        <Input
          prefixClassName="text-content_primary"
          placeholder={I18n.t("Search Stock / Crypto")}
          onChange={(e) => handleSearchInput(e)}
          onClear={handleClearSearch}
          containerClassName={cn("h-9 bg-background_solid_tertiary ", {
            "border-background_white_overlay20": onFocus,
            "border-background_solid_secondary": !onFocus,
          })}
          autoFocus={false}
          onFocus={(focus) => {
            setOnFocus(focus);
          }}
        />
        {showCancel && (
          <div
            data-openreplay-label={`QuickInsights · QuickSearchNormalComponent · div | ${I18n.t("Cancel")}`}
            className="body_s text-content_tertiary cursor-pointer min-w-fit hover:text-content_secondary transition-colors"
            onClick={() => onClose?.()}
          >
            {I18n.t("Cancel")}
          </div>
        )}
      </div>

      {/* 返回按钮区域 - sticky吸顶，在搜索框下方 */}

      {/* 统一的滚动容器 */}
      <div
        ref={contentScrollRef}
        className={cn(
          "overflow-y-auto no-scrollbar flex-1 h-[460px] min-h-[460px]",
          {
            "max-h-[460px]": keywords.trim(),
          },
        )}
      >
        {keywords.trim() &&
          viewState === ViewState.CATEGORY_DETAIL &&
          selectedCategory && (
            <div
              className={cn(
                "sticky top-0 z-10 bg-background_solid_secondary pb-1 flex items-center gap-3",
              )}
            >
              <button
                data-openreplay-label="Back to search overview Button"
                id="btn-back-to-search-overview"
                onClick={handleBack}
                className="flex items-center gap-2 text-content_primary hover:text-content_secondary transition-colors py-1"
                aria-label="Back to search overview"
              >
                <Icon
                  name="left-arrow-alt"
                  size={16}
                  color={ospThemes.colors.content_primary}
                />
                <span className="body_s">{I18n.t("Back")}</span>
              </button>
            </div>
          )}
        {/* 内容区域 */}
        {keywords.trim() ? (
          viewState === ViewState.SEARCH_OVERVIEW ? (
            <div>
              <SearchOverview
                stockSearchData={stockSearchData}
                cryptoSearchData={cryptoSearchData}
                searchLoading={searchLoading}
                onViewAll={handleViewAll}
                onItemClick={handleItemClick}
              />
            </div>
          ) : (
            <div>
              <CategoryDetail
                selectedCategory={selectedCategory}
                stockSearchData={stockSearchData}
                cryptoSearchData={cryptoSearchData}
                searchLoading={searchLoading}
                onItemClick={handleItemClick}
              />
            </div>
          )
        ) : (
          <div>
            <RecommendedArea
              recommendedTab={recommendedTab}
              onTabChange={setRecommendedTab}
              hotPageData={hotPageData}
              hotPageDataLoading={hotPageDataLoading}
              trendingProjectsDataSet={trendingProjectsDataSet}
              hotXRequestAgentLoading={hotXRequestAgentLoading}
              stockTrending={stockTrending}
              stockTrendingRequestAgentLoading={
                stockTrendingRequestAgentLoading
              }
              onItemClick={handleItemClick}
              title={title}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickSearchNormalComponent;
